import { useEffect, useMemo, useRef, useState } from 'react';
import { getDocs, onSnapshot } from 'firebase/firestore';

/**
 * Live Firestore document. `ref` may be null (nothing to load). Re-subscribes only
 * when the document path changes.
 */
export function useDoc(ref) {
  const path = ref ? ref.path : null;
  const [state, setState] = useState({ data: undefined, exists: false, loading: !!path, error: null });
  const refHolder = useRef(ref);
  refHolder.current = ref;
  useEffect(() => {
    if (!path) {
      setState({ data: null, exists: false, loading: false, error: null });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true }));
    return onSnapshot(
      refHolder.current,
      (snap) => setState({ data: snap.exists() ? { id: snap.id, ...snap.data() } : null, exists: snap.exists(), loading: false, error: null }),
      (error) => setState({ data: null, exists: false, loading: false, error }),
    );
  }, [path]);
  return state;
}

/** Live query/collection. `key` must uniquely describe the query (it gates re-subscription). */
export function useCollection(q, key) {
  const [state, setState] = useState({ docs: [], loading: !!q, error: null });
  const qHolder = useRef(q);
  qHolder.current = q;
  useEffect(() => {
    if (!key || !qHolder.current) {
      setState({ docs: [], loading: false, error: null });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true }));
    return onSnapshot(
      qHolder.current,
      (snap) => setState({ docs: snap.docs.map((d) => ({ id: d.id, ...d.data() })), loading: false, error: null }),
      (error) => setState({ docs: [], loading: false, error }),
    );
  }, [key]);
  return state;
}

/**
 * One-shot query (no listener). Used for other users' data (trainer → athlete): a
 * listener that gets permission-denied trips a Firestore SDK internal assertion
 * ("Unexpected state ca9") when React re-subscribes it, so read once instead.
 */
export function useCollectionOnce(q, key) {
  const [state, setState] = useState({ docs: [], loading: !!q, error: null });
  const qHolder = useRef(q);
  qHolder.current = q;
  useEffect(() => {
    if (!key || !qHolder.current) {
      setState({ docs: [], loading: false, error: null });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    getDocs(qHolder.current)
      .then((snap) => alive && setState({ docs: snap.docs.map((d) => ({ id: d.id, ...d.data() })), loading: false, error: null }))
      .catch((error) => alive && setState({ docs: [], loading: false, error }));
    return () => { alive = false; };
  }, [key]);
  return state;
}

let exerciseCache = null;
let exercisePromise = null;

export function loadExercises() {
  if (exerciseCache) return Promise.resolve(exerciseCache);
  if (!exercisePromise) {
    exercisePromise = fetch('/exercises.json')
      .then((r) => r.json())
      .then((list) => {
        const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
        exerciseCache = { list: sorted, byId: Object.fromEntries(sorted.map((e) => [e.id, e])) };
        return exerciseCache;
      })
      .catch(() => {
        exercisePromise = null;
        return { list: [], byId: {} };
      });
  }
  return exercisePromise;
}

export function useExercises() {
  const [data, setData] = useState(exerciseCache || { list: [], byId: {} });
  useEffect(() => {
    let alive = true;
    loadExercises().then((d) => alive && setData(d));
    return () => { alive = false; };
  }, []);
  return data;
}

export function exerciseImage(ex) {
  return ex && ex.imagePath ? `/${ex.imagePath}` : null;
}

/** Web plays the mp4 (`videoPath`); `gifPath` is for the mobile app. */
export function exerciseVideo(ex) {
  return ex && /\.mp4$/i.test(ex.videoPath || '') ? `/${ex.videoPath}` : null;
}

export function useMediaQuery(q) {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false);
  const [m, setM] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const fn = () => setM(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [q]);
  return m;
}

/** Re-renders at local midnight so "today" keys roll over without a reload. */
export function useToday() {
  const [today, setToday] = useState(() => new Date());
  useEffect(() => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5);
    const id = setTimeout(() => setToday(new Date()), next - now);
    return () => clearTimeout(id);
  }, [today]);
  return useMemo(() => today, [today]);
}
